import React from "react";
import {
    Building2,
    Sparkles,
    HandshakeIcon,
    Briefcase,
    MapPin,
    Mail,
    ArrowUpRight,
    Check,
} from "lucide-react";
import { FadeUpGroup } from "../components/FadeUpGroup";
import { WhatsInAName } from "../components/WhatsInAName";

const TENETS = [
    {
        h: "Pricing is a product decision.",
        p: "No \"Contact sales\" theatre. Every product has one price on the pricing page. Every bundle discount is automatic. Every customer pays the same rate.",
    },
    {
        h: "Boardroom-grade or not built.",
        p: "Anything that ships in a Scitus product has to stand up to a CFO, an auditor, and a board pack. If it can't, we don't ship it.",
    },
    {
        h: "Operators over architects.",
        p: "We hire former founders, heads-of-ops, finance leads, and category managers. Every product manager has run a real business or a real function. Code is downstream of context.",
    },
    {
        h: "Honest about the boring stuff.",
        p: "Real screenshots, not mockups. Real customer names stay private until they choose to tell their own story. Real numbers, not testimonials we wrote ourselves.",
    },
];

const FACTS = [
    "7 products live · more in design",
    "Paying customers across retail, real estate, manufacturing, professional services, and healthcare",
    "Every product runs on the same platform spine — SSO, unified billing, shared audit, one API",
    "Human support included on every plan — no support-as-a-product games",
];

const OFFICES = [
    { city: "Chennai", country: "India", note: "Engineering · Product · Customer-engineering" },
    { city: "Austin", country: "USA", note: "Customer development · Partnerships" },
];

export const AboutPage = () => {
    return (
        <section className="section about-page" data-testid="about-page">
            <div className="container" style={{ maxWidth: 900 }}>
                <FadeUpGroup>
                    {/* Eyebrow */}
                    <div className="fade-up">
                        <span
                            className="pill-accent"
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 8,
                                padding: "6px 14px",
                                borderRadius: 999,
                                background: "var(--accent-soft)",
                                color: "var(--accent)",
                                fontSize: "0.78rem",
                                fontWeight: 600,
                                letterSpacing: "0.06em",
                                textTransform: "uppercase",
                            }}
                            data-testid="about-eyebrow"
                        >
                            <Building2 size={14} /> About Scitus.ai
                        </span>
                    </div>

                    {/* H1 */}
                    <h1
                        className="h1 fade-up"
                        style={{
                            fontSize: "clamp(2.25rem, 5vw, 3.75rem)",
                            lineHeight: 1.05,
                            margin: "20px 0 18px 0",
                            maxWidth: 820,
                        }}
                        data-testid="about-h1"
                    >
                        Operator software, built by operators.
                    </h1>

                    <p
                        className="muted fade-up"
                        style={{
                            fontSize: "1.1rem",
                            lineHeight: 1.65,
                            maxWidth: 760,
                            margin: 0,
                        }}
                    >
                        Most B2B SaaS is built by founders who've never had to close a month, defend a working-capital
                        spike to a finance partner, or sign off on a vendor PO at midnight. We have. That's where
                        Scitus.ai started.
                    </p>

                    {/* Intro block */}
                    <div
                        className="card fade-up"
                        style={{ marginTop: 36, padding: "32px 32px" }}
                        data-testid="about-intro-card"
                    >
                        <p
                            style={{
                                color: "var(--text)",
                                fontSize: "1rem",
                                lineHeight: 1.75,
                                margin: 0,
                            }}
                        >
                            We build premium operating software for small and mid-sized businesses — the teams that grew
                            faster than their tooling. Today, that's seven standalone products: real-time business
                            intelligence (<strong>Mativex</strong>), live operations command (<strong>Vexen</strong>),
                            HR &amp; people ops (<strong>Karya</strong>), GST-ready billing &amp; finance{" "}
                            (<strong>Vyaahar</strong>), audit-grade e-signing (<strong>Attestra</strong>), project
                            cost &amp; procurement (<strong>Acrium</strong>), and procurement intelligence for category
                            teams (<strong>Kretara</strong>). A few more are in design.
                        </p>
                    </div>

                    {/* Bouquet thesis */}
                    <div className="fade-up" style={{ marginTop: 56 }}>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 12,
                                marginBottom: 14,
                            }}
                        >
                            <span
                                style={{
                                    width: 44,
                                    height: 44,
                                    borderRadius: 12,
                                    background: "var(--accent-soft)",
                                    color: "var(--accent)",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <Sparkles size={20} />
                            </span>
                            <h2
                                className="h2"
                                style={{
                                    fontSize: "clamp(1.6rem, 3vw, 2.25rem)",
                                    margin: 0,
                                }}
                            >
                                The bouquet thesis.
                            </h2>
                        </div>
                        <p
                            className="muted"
                            style={{ fontSize: "0.98rem", lineHeight: 1.75, margin: 0 }}
                        >
                            Most "all-in-one" platforms force you to swallow seven half-built modules to get the one
                            thing you actually need. Most point tools force you into seven disconnected logins, seven
                            data silos, and seven reconciliation jobs at month-end.
                        </p>
                        <p
                            className="muted"
                            style={{ fontSize: "0.98rem", lineHeight: 1.75, margin: "16px 0 0 0" }}
                        >
                            We chose a third path. Every Scitus product is a{" "}
                            <span style={{ color: "var(--text)", fontWeight: 600 }}>
                                complete, standalone operator product
                            </span>{" "}
                            — sharp enough to win on its own merits in its own category. The moment you add a second,
                            the platform earns its name: shared identity, shared data, shared billing, shared audit
                            log — and every product on your account automatically drops 25%. Buy what you need. Add
                            what you grow into. Never pay for shelfware again.
                        </p>
                    </div>

                    {/* Built by Vyastics */}
                    <div className="fade-up" style={{ marginTop: 56 }}>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 12,
                                marginBottom: 14,
                            }}
                        >
                            <span
                                style={{
                                    width: 44,
                                    height: 44,
                                    borderRadius: 12,
                                    background: "var(--accent-soft)",
                                    color: "var(--accent)",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <Briefcase size={20} />
                            </span>
                            <h2
                                className="h2"
                                style={{
                                    fontSize: "clamp(1.6rem, 3vw, 2.25rem)",
                                    margin: 0,
                                }}
                            >
                                Built by Vyastics.
                            </h2>
                        </div>
                        <p
                            className="muted"
                            style={{ fontSize: "0.98rem", lineHeight: 1.75, margin: 0 }}
                        >
                            Scitus.ai is developed by{" "}
                            <span style={{ color: "var(--text)", fontWeight: 600 }}>
                                Vyastics Technologies Private Limited
                            </span>{" "}
                            — a software studio with a stubborn opinion: that operator-grade software for SMEs deserves
                            the same craftsmanship as enterprise software, at a price an SME actually pays.
                        </p>
                        <p
                            className="muted"
                            style={{ fontSize: "0.98rem", lineHeight: 1.75, margin: "16px 0 0 0" }}
                        >
                            Vyastics writes the products, runs the platform, and owns the customer relationship
                            end-to-end. No resellers, no white-labels, no offshore subcontractors between the team
                            building Scitus and the people using it. When something breaks at 9 PM on a Tuesday, the
                            person who wrote the code is the person who picks up the phone.
                        </p>

                        {/* Offices */}
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                                gap: 14,
                                marginTop: 26,
                            }}
                            data-testid="about-offices"
                        >
                            {OFFICES.map((o) => (
                                <div
                                    key={o.city}
                                    className="card"
                                    style={{ padding: "20px 22px" }}
                                    data-testid={`about-office-${o.city.toLowerCase()}`}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 10,
                                            marginBottom: 8,
                                        }}
                                    >
                                        <MapPin size={16} style={{ color: "var(--accent)" }} />
                                        <span
                                            style={{
                                                fontFamily: "'Cabinet Grotesk'",
                                                fontWeight: 700,
                                                color: "var(--text)",
                                                fontSize: "1.05rem",
                                            }}
                                        >
                                            {o.city}, {o.country}
                                        </span>
                                    </div>
                                    <p
                                        className="muted"
                                        style={{ margin: 0, fontSize: "0.88rem", lineHeight: 1.55 }}
                                    >
                                        {o.note}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* What we believe */}
                    <div className="fade-up" style={{ marginTop: 56 }}>
                        <h2
                            className="h2"
                            style={{
                                fontSize: "clamp(1.6rem, 3vw, 2.25rem)",
                                margin: "0 0 22px 0",
                            }}
                        >
                            What we believe.
                        </h2>
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                                gap: 16,
                            }}
                            data-testid="about-tenets"
                        >
                            {TENETS.map((t, i) => (
                                <div
                                    key={t.h}
                                    className="card"
                                    style={{ padding: "24px 24px" }}
                                    data-testid={`about-tenet-${i + 1}`}
                                >
                                    <h3
                                        style={{
                                            fontFamily: "'Cabinet Grotesk'",
                                            fontWeight: 700,
                                            color: "var(--text)",
                                            margin: "0 0 10px 0",
                                            fontSize: "1.1rem",
                                            lineHeight: 1.3,
                                        }}
                                    >
                                        {t.h}
                                    </h3>
                                    <p
                                        className="muted"
                                        style={{
                                            margin: 0,
                                            fontSize: "0.93rem",
                                            lineHeight: 1.65,
                                        }}
                                    >
                                        {t.p}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* The work, today */}
                    <div className="fade-up" style={{ marginTop: 56 }}>
                        <h2
                            className="h2"
                            style={{
                                fontSize: "clamp(1.6rem, 3vw, 2.25rem)",
                                margin: "0 0 22px 0",
                            }}
                        >
                            The work, today.
                        </h2>
                        <ul
                            style={{
                                listStyle: "none",
                                padding: 0,
                                margin: 0,
                                display: "grid",
                                gap: 12,
                            }}
                            data-testid="about-facts"
                        >
                            {FACTS.map((f) => (
                                <li
                                    key={f}
                                    style={{
                                        display: "flex",
                                        gap: 12,
                                        alignItems: "flex-start",
                                        padding: "14px 18px",
                                        background: "var(--surface)",
                                        border: "1px solid var(--border)",
                                        borderRadius: 12,
                                    }}
                                >
                                    <span
                                        style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            width: 22,
                                            height: 22,
                                            borderRadius: 999,
                                            background: "var(--accent-soft)",
                                            color: "var(--accent)",
                                            flexShrink: 0,
                                            marginTop: 2,
                                        }}
                                    >
                                        <Check size={13} strokeWidth={3} />
                                    </span>
                                    <span
                                        style={{
                                            color: "var(--text)",
                                            fontSize: "0.96rem",
                                            lineHeight: 1.55,
                                        }}
                                    >
                                        {f}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Work with us */}
                    <div className="fade-up" style={{ marginTop: 56 }}>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 12,
                                marginBottom: 16,
                            }}
                        >
                            <span
                                style={{
                                    width: 44,
                                    height: 44,
                                    borderRadius: 12,
                                    background: "var(--accent-soft)",
                                    color: "var(--accent)",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <HandshakeIcon size={20} />
                            </span>
                            <h2
                                className="h2"
                                style={{
                                    fontSize: "clamp(1.6rem, 3vw, 2.25rem)",
                                    margin: 0,
                                }}
                            >
                                Work with us.
                            </h2>
                        </div>
                        <div
                            className="card"
                            style={{ padding: "28px 30px" }}
                            data-testid="about-work-with-us"
                        >
                            <ul
                                style={{
                                    listStyle: "none",
                                    padding: 0,
                                    margin: 0,
                                    display: "grid",
                                    gap: 14,
                                }}
                            >
                                <li style={{ display: "grid", gap: 4 }}>
                                    <span
                                        style={{
                                            color: "var(--text)",
                                            fontFamily: "'Cabinet Grotesk'",
                                            fontWeight: 700,
                                            fontSize: "1rem",
                                        }}
                                    >
                                        Customers
                                    </span>
                                    <span className="muted" style={{ fontSize: "0.94rem" }}>
                                        Book a demo, or just hit the launch button on any product page.
                                    </span>
                                </li>
                                <li style={{ display: "grid", gap: 4 }}>
                                    <span
                                        style={{
                                            color: "var(--text)",
                                            fontFamily: "'Cabinet Grotesk'",
                                            fontWeight: 700,
                                            fontSize: "1rem",
                                        }}
                                    >
                                        Partners
                                    </span>
                                    <span className="muted" style={{ fontSize: "0.94rem" }}>
                                        Resellers, integrators, and advisors — write to{" "}
                                        <a href="mailto:hello@scitus.ai" style={{ color: "var(--accent)" }}>
                                            hello@scitus.ai
                                        </a>
                                        .
                                    </span>
                                </li>
                                <li style={{ display: "grid", gap: 4 }}>
                                    <span
                                        style={{
                                            color: "var(--text)",
                                            fontFamily: "'Cabinet Grotesk'",
                                            fontWeight: 700,
                                            fontSize: "1rem",
                                        }}
                                    >
                                        Careers
                                    </span>
                                    <span className="muted" style={{ fontSize: "0.94rem" }}>
                                        Engineering, product, design, customer-engineering. Remote-friendly. Write
                                        to{" "}
                                        <a href="mailto:hello@scitus.ai" style={{ color: "var(--accent)" }}>
                                            hello@scitus.ai
                                        </a>
                                        .
                                    </span>
                                </li>
                                <li style={{ display: "grid", gap: 4 }}>
                                    <span
                                        style={{
                                            color: "var(--text)",
                                            fontFamily: "'Cabinet Grotesk'",
                                            fontWeight: 700,
                                            fontSize: "1rem",
                                        }}
                                    >
                                        Press &amp; writers
                                    </span>
                                    <span className="muted" style={{ fontSize: "0.94rem" }}>
                                        Stories, interviews, podcasts —{" "}
                                        <a href="mailto:hello@scitus.ai" style={{ color: "var(--accent)" }}>
                                            hello@scitus.ai
                                        </a>
                                        .
                                    </span>
                                </li>
                            </ul>

                            <hr className="divider" style={{ margin: "26px 0 22px 0" }} />

                            <div
                                style={{
                                    display: "flex",
                                    gap: 12,
                                    flexWrap: "wrap",
                                    alignItems: "center",
                                }}
                            >
                                <a
                                    href="/contact"
                                    className="btn btn-primary"
                                    data-testid="about-cta-book-demo"
                                >
                                    Book a demo <ArrowUpRight size={16} />
                                </a>
                                <a
                                    href="mailto:hello@scitus.ai"
                                    className="btn btn-ghost"
                                    data-testid="about-cta-email"
                                >
                                    <Mail size={15} /> hello@scitus.ai
                                </a>
                            </div>
                        </div>
                    </div>

                    {/* "What's in a name?" — etymology section */}
                    <WhatsInAName />

                    {/* Legal footer line */}
                    <p
                        className="muted fade-up"
                        style={{
                            fontSize: "0.85rem",
                            marginTop: 40,
                            textAlign: "center",
                        }}
                        data-testid="about-legal-line"
                    >
                        Scitus.ai is a product of <strong>Vyastics Technologies Private Limited</strong>.<br />
                        Offices in Chennai, India · Austin, USA.
                    </p>
                </FadeUpGroup>
            </div>
        </section>
    );
};

export default AboutPage;
